import { RestartAltRounded } from '@mui/icons-material'
import { IconButton, Tooltip } from '@mui/material'
import { ReactNode } from 'react'
import { Box, BoxProps, List, Typography } from '@mui/material'

/**
 * Header and container for a list of filter list items
 *
 * Expects 2 props, and a list of <FilterListItem> as children:
 *
 * - label: The label for this filter section. Will be translated.
 * - icon: An icon react element
 *
 * @see FilterListItem
 *
 * @example
 *
 * import * as React from 'react';
 * import { Card, CardContent } from '@mui/material';
 * import MailIcon from '@mui/icons-material/MailOutline';
 * import { FilterList, FilterListItem } from 'react-admin';
 *
 * const FilterSidebar = () => (
 *     <Card>
 *         <CardContent>
 *             <FilterList
 *                 label="Subscribed to newsletter"
 *                 icon={<MailIcon />}
 *             >
 *                 <FilterListItem
 *                     label="Yes"
 *                     value={{ has_newsletter: true }}
 *                  />
 *                 <FilterListItem
 *                     label="No"
 *                     value={{ has_newsletter: false }}
 *                  />
 *             </FilterList>
 *         </CardContent>
 *     </Card>
 * );
 */
export const FilterList = (props: FilterListProps) => {
  const { label, icon, children, onReset, resetDisabled = false, resetTooltip = 'Réinitialiser', ...rest } = props
  return (
    <Box {...rest}>
      <Box mt={2} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center" minWidth={0}>
          <Box mr={1}>{icon}</Box>
          <Typography
            variant="overline"
            sx={{
              fontWeight: 400,
              color: 'grey.700',
              marginTop: -1,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
        </Box>
        {onReset ? (
          <Tooltip title={resetTooltip}>
            <span>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  onReset()
                }}
                disabled={resetDisabled}
                sx={{ color: '#7A8197', width: 24, height: 24 }}
              >
                <RestartAltRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Box>
      <List dense disablePadding>
        {children}
      </List>
    </Box>
  )
}

export interface FilterListProps extends BoxProps {
  label: string
  icon: ReactNode
  onReset?: () => void
  resetDisabled?: boolean
  resetTooltip?: string
}
